import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingUp, FiZap, FiTarget, FiAward, FiStar } = FiIcons;

const HeroSection = () => {
  return (
    <section className="relative min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-40 left-20 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="relative z-10 container mx-auto px-6 pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen">
          {/* Left Content */}
          <motion.div 
            className="text-white space-y-8"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Trust Badges */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex items-center space-x-1">
                {[1,2,3,4,5].map((star) => (
                  <SafeIcon key={star} icon={FiStar} className="w-5 h-5 text-yellow-400 fill-current" />
                ))}
              </div>
              <span className="text-sm bg-white/20 px-3 py-1 rounded-full backdrop-blur">
                4.9/5 ★ Rated by 500+ MSMEs
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
              Grow Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-400">MSME Business</span> Using AI Funnels & WhatsApp Automation
            </h1>
            
            <p className="text-xl lg:text-2xl font-medium text-gray-300">
              Without Hiring More Staff or Wasting Money on Agencies
            </p>

            <p className="text-lg text-gray-200 max-w-2xl">
              A complete all-in-one sales, marketing, automation & customer retention system designed especially for <strong>Busy MSME Owners</strong> looking to scale revenue with less stress.
            </p>

            {/* CTA Button */}
            <motion.div 
              className="pt-4"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <a 
                href="https://funnelsdone.com/cash" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold text-lg rounded-full hover:from-yellow-400 hover:to-orange-400 transition-all duration-300 shadow-2xl hover:shadow-yellow-500/25"
              >
                👉 Book a Free Strategy Call
                <SafeIcon icon={FiTarget} className="ml-2 w-5 h-5" />
              </a>
            </motion.div>

            {/* Social Proof */}
            <div className="flex items-center space-x-8 pt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">500+</div>
                <div className="text-sm text-gray-300">Happy MSMEs</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">300%</div>
                <div className="text-sm text-gray-300">Avg Revenue Growth</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-400">24/7</div>
                <div className="text-sm text-gray-300">AI Support</div>
              </div>
            </div>
          </motion.div>

          {/* Right Visual */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20">
              {/* Dashboard Preview */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-white font-semibold text-lg">Business Dashboard</h3>
                  <div className="flex space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 p-4 rounded-lg border border-white/10">
                    <SafeIcon icon={FiTrendingUp} className="w-8 h-8 text-blue-400 mb-2" />
                    <div className="text-white text-sm">Monthly Revenue</div>
                    <div className="text-2xl font-bold text-white">₹2.5L</div>
                    <div className="text-green-400 text-xs">↗ +45%</div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 p-4 rounded-lg border border-white/10">
                    <SafeIcon icon={FiZap} className="w-8 h-8 text-green-400 mb-2" />
                    <div className="text-white text-sm">Auto Leads</div>
                    <div className="text-2xl font-bold text-white">127</div>
                    <div className="text-green-400 text-xs">This month</div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="space-y-3">
                  <h4 className="text-white font-medium">Recent AI Activity</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3 bg-white/5 p-3 rounded-lg">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-gray-300 text-sm">WhatsApp auto-reply sent to lead #127</span>
                    </div>
                    <div className="flex items-center space-x-3 bg-white/5 p-3 rounded-lg">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                      <span className="text-gray-300 text-sm">New funnel visitor converted</span>
                    </div>
                    <div className="flex items-center space-x-3 bg-white/5 p-3 rounded-lg">
                      <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                      <span className="text-gray-300 text-sm">Appointment booked automatically</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <motion.div 
              className="absolute -top-4 -right-4 bg-yellow-500 p-3 rounded-full"
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <SafeIcon icon={FiAward} className="w-6 h-6 text-black" />
            </motion.div>

            <motion.div 
              className="absolute -bottom-4 -left-4 bg-pink-500 p-3 rounded-full"
              animate={{ y: [10, -10, 10] }}
              transition={{ duration: 3, repeat: Infinity, delay: 1 }}
            >
              <SafeIcon icon={FiZap} className="w-6 h-6 text-white" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;